import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Link,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Snackbar,
  CircularProgress,
} from "@mui/material";

import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { login } from "../Slices/authSlice";
import { useForm } from "react-hook-form";
import { fetchUserAttributes, signIn, signOut } from "@aws-amplify/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormFieldTextField } from "components/shared/components/FormFieldTextField";
import { signInSchema } from "lib/schema";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  useLazyGetVerificationStatusQuery,
  useLazyGetOnboardingStatusQuery,
} from "components/Slices/usersApiSlice";
import { usersApiSlice } from "../Slices/usersApiSlice";

interface SignInFormValues {
  email: string;
  password: string;
}

export const SignIn = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [getVerificationStatus] = useLazyGetVerificationStatusQuery();
  const [getOnboardingStatus] = useLazyGetOnboardingStatusQuery();
  const [checkUserExists] = usersApiSlice.useLazyEmailExistsQuery();

  // Determine which onboarding step a user should resume at
  // New flow: 0=Welcome, 1=Contact, 2=DOB, 3=Terms, 4=Verify, 5=Payment
  const getOnboardingStep = async (): Promise<number> => {
    try {
      const onboarding = await getOnboardingStatus().unwrap();
      const status = onboarding?.status?.toLowerCase();

      // If verifying/active/complete/failed — go to verification step
      if (
        status === "verifying" ||
        status === "active" ||
        status === "complete" ||
        status === "verification_failed"
      ) {
        return 4;
      }

      // Check progressive completion: address+phone → DOB → terms → verify
      if (!onboarding?.hasAddress || !onboarding?.hasPhone) {
        return 1; // Contact Info step
      }
      if (!onboarding?.hasDob) {
        return 2; // DOB step
      }

      // If has all basic info but not verified, go to terms or verify
      return 3;
    } catch {
      // If we can't get onboarding status, start from welcome
      return 0;
    }
  };

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await fetchUserAttributes();
        if (user) {
          const { data: userExistsData } = await checkUserExists({
            email: user.email || "",
          });

          if (!userExistsData?.exists) {
            await signOut();
            setMessage(t("auth.signIn.accountNotFound"));
            return;
          }

          dispatch(
            login({
              id: user.sub || "",
              email: user.email || "",
              firstName: user.given_name || "",
              lastName: user.family_name || "",
              displayImg: user.displayImg || "",
            })
          );

          try {
            const status = await getVerificationStatus().unwrap();
            if (status.verification.status === "verified") {
              navigate("/view-group");
              return;
            }
          } catch {
            // Not verified — determine which onboarding step to resume
          }

          const step = await getOnboardingStep();
          navigate(`/onboarding?step=${step}`);
        }
      } catch (error) {
        console.error("Error fetching user attributes:", error);
      }
    };
    checkUser();
  }, [navigate, checkUserExists, dispatch]);

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const handleClickShowPassword = () => {
    setShowPassword((prev: boolean) => !prev);
  };

  const { control, handleSubmit } = useForm<SignInFormValues>({
    mode: "all",
    resolver: zodResolver(signInSchema),
  });

  const signInSubmit = async (data: SignInFormValues) => {
    setLoading(true);
    try {
      const res = await signIn({
        username: data.email,
        password: data.password,
      });
      if (res.isSignedIn) {
        const attributes = await fetchUserAttributes();

        const { data: userExistsData } = await checkUserExists({
          email: attributes.email || data.email,
        });

        if (!userExistsData?.exists) {
          await signOut();
          setMessage(t("auth.signIn.accountNotFound"));
          return;
        }

        dispatch(
          login({
            id: attributes.sub || "",
            email: attributes.email || "",
            firstName: attributes.given_name || "",
            lastName: attributes.family_name || "",
            displayImg: attributes.profile || "",
          })
        );

        // Check if onboarding is complete
        try {
          const status = await getVerificationStatus().unwrap();
          if (status.verification.status !== "verified") {
            const step = await getOnboardingStep();
            navigate(`/onboarding?step=${step}`);
            return;
          }
        } catch {
          const step = await getOnboardingStep();
          navigate(`/onboarding?step=${step}`);
          return;
        }
      } else {
        if (res.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
          localStorage.setItem("pendingEmail", data.email);
          navigate(`/confirm`, {
            state: {
              email: data.email,
              tempPassword: data.password,
            },
          });
        }
        setMessage(t("auth.signIn.failed"));
      }
    } catch (error) {
      if (error instanceof Error) {
        setMessage(error.message);
      } else {
        setMessage(t("common.genericError"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: '60%', px: 3 }}>
      <Typography
        component="h1"
        sx={{
          fontSize: "1.65rem",
          fontWeight: 700,
          color: "text.primary",
          letterSpacing: "-0.01em",
          mb: 0.5,
        }}
      >
        {t("auth.signIn.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5 }}>
        {t("auth.signIn.subtitle")}
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(signInSubmit)}
        noValidate
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <FormFieldTextField
          control={control}
          name="email"
          label={t("common.emailLabel")}
          required
          fullWidth
          id="email"
          InputLabelProps={{ shrink: true }}
        />
        <FormFieldTextField
          control={control}
          required
          fullWidth
          name="password"
          label={t("auth.signIn.passwordLabel")}
          type={showPassword ? "text" : "password"}
          id="password"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  size="small"
                  edge="end"
                >
                  {showPassword ? (
                    <Visibility fontSize="small" />
                  ) : (
                    <VisibilityOff fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
          <Link
            component={RouterLink}
            to="/forgot-password"
            sx={{
              fontSize: "0.8rem",
              color: "text.secondary",
              textDecoration: "none",
              "&:hover": { color: "primary.main" },
            }}
          >
            {t("auth.signIn.forgotPassword")}
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ py: 1.25, fontSize: "0.95rem", fontWeight: 600 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : t("auth.signIn.submit")}
        </Button>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 3, textAlign: "center" }}
      >
        {t("auth.signIn.noAccount")}{" "}
        <Link
          component={RouterLink}
          to="/register"
          sx={{ color: "primary.main", fontWeight: 600, textDecoration: "none" }}
        >
          {t("auth.signIn.createOne")}
        </Link>
      </Typography>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={message !== null}
        autoHideDuration={5000}
        message={message}
        onClose={() => setMessage(null)}
        ContentProps={{
          sx: {
            borderRadius: "5px",
            color: "white",
            bgcolor: "error.main",
            fontWeight: "bold",
            fontSize: "1rem",
          },
        }}
      />
    </Box>
  );
};

export default SignIn;
